/**
 * PublishedContractSignersConfig
 * Subcomponente para configuração dos signatários padrão de um contrato publicado
 * Permite adicionar, remover e reordenar papéis de assinatura
 * Suporta dados padrão (nome, email, telefone) que serão pré-preenchidos no wizard
 * 
 * RELEASE: v1.1.0 - 2026-03-21
 * - v1.1.0: Adicionado campos de dados padrão (nome, email, telefone) expandíveis por signatário
 * - v1.0.0: Versão inicial com ordenação e método de entrega
 */

import React, { useState } from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { PhoneInput } from '@/components/ui/phone-input';
import { SIGNER_ROLES } from '@/types/contracts';
import type { DefaultSigner } from '@/types/contracts';
import { cn } from '@/lib/utils';

// Métodos de entrega disponíveis
const DELIVERY_METHODS = [
  { value: 'email', label: 'E-mail' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'sms', label: 'SMS' },
] as const;

interface PublishedContractSignersConfigProps {
  /** Lista atual de signatários padrão */
  signers: DefaultSigner[];
  /** Callback ao alterar a lista */
  onChange: (signers: DefaultSigner[]) => void;
  /** Métodos permitidos baseados no provider selecionado */
  allowedMethods: ('email' | 'whatsapp' | 'sms')[];
}

/**
 * Configuração de signatários padrão com ordem, método de entrega e dados pré-preenchidos
 */
export function PublishedContractSignersConfig({
  signers,
  onChange,
  allowedMethods,
}: PublishedContractSignersConfigProps) {
  // Controla qual signatário está expandido para editar dados padrão
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  // Papéis ainda não adicionados
  const availableRoles = SIGNER_ROLES.filter(
    (r) => !signers.some((s) => s.signer_role === r.value)
  );

  // Adiciona novo signatário com papel selecionado
  const handleAddSigner = () => {
    if (availableRoles.length === 0) return;
    const firstAvailable = availableRoles[0];
    const newSigner: DefaultSigner = {
      sign_order: signers.length + 1,
      signer_role: firstAvailable.value,
      delivery_method: allowedMethods[0] || 'email',
    };
    onChange([...signers, newSigner]);
    // Expande o novo signatário automaticamente
    setExpandedIndex(signers.length);
  };

  // Remove signatário pelo índice e reordena
  const handleRemove = (index: number) => {
    const updated = signers
      .filter((_, i) => i !== index)
      .map((s, i) => ({ ...s, sign_order: i + 1 }));
    onChange(updated);
    // Fecha expansão se o removido estava expandido
    if (expandedIndex === index) setExpandedIndex(null);
    else if (expandedIndex !== null && expandedIndex > index) {
      setExpandedIndex(expandedIndex - 1);
    }
  };

  // Move signatário para cima na ordem
  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const updated = [...signers];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    onChange(updated.map((s, i) => ({ ...s, sign_order: i + 1 })));
    // Atualiza índice expandido junto com a movimentação
    if (expandedIndex === index) setExpandedIndex(index - 1);
    else if (expandedIndex === index - 1) setExpandedIndex(index);
  };

  // Move signatário para baixo na ordem
  const handleMoveDown = (index: number) => {
    if (index === signers.length - 1) return;
    const updated = [...signers];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    onChange(updated.map((s, i) => ({ ...s, sign_order: i + 1 })));
    // Atualiza índice expandido junto com a movimentação
    if (expandedIndex === index) setExpandedIndex(index + 1);
    else if (expandedIndex === index + 1) setExpandedIndex(index);
  };

  // Altera papel do signatário no índice
  const handleRoleChange = (index: number, role: string) => {
    const updated = signers.map((s, i) =>
      i === index ? { ...s, signer_role: role } : s
    );
    onChange(updated);
  };

  // Altera método de entrega do signatário no índice
  const handleMethodChange = (index: number, method: string) => {
    const updated = signers.map((s, i) =>
      i === index ? { ...s, delivery_method: method as DefaultSigner['delivery_method'] } : s
    );
    onChange(updated);
  };

  // Altera campo de dados padrão do signatário (nome, email, telefone)
  const handleDefaultFieldChange = (index: number, field: 'default_name' | 'default_email' | 'default_phone', value: string) => {
    const updated = signers.map((s, i) =>
      i === index ? { ...s, [field]: value } : s
    );
    onChange(updated);
  };

  // Alterna expansão do card
  const toggleExpand = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  // Filtra métodos de entrega de acordo com os permitidos
  const visibleMethods = DELIVERY_METHODS.filter((m) =>
    allowedMethods.includes(m.value)
  );

  return (
    <div className="space-y-4">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-sm font-medium">Sequência de Assinaturas</Label>
          <p className="text-xs text-muted-foreground mt-0.5">
            Configure a ordem, método de entrega e dados padrão de cada signatário
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAddSigner}
          disabled={availableRoles.length === 0}
          className="gap-1.5"
        >
          <Plus className="h-3.5 w-3.5" />
          Adicionar
        </Button>
      </div>

      {/* Lista de signatários */}
      {signers.length === 0 ? (
        <div className="py-8 text-center text-sm text-muted-foreground border border-dashed rounded-lg">
          Nenhum signatário configurado. Clique em Adicionar para começar.
        </div>
      ) : (
        <div className="space-y-2">
          {signers.map((signer, index) => {
            // Papéis disponíveis para este signatário (excluindo os já usados, exceto o próprio)
            const rolesForThis = SIGNER_ROLES.filter(
              (r) =>
                r.value === signer.signer_role ||
                !signers.some((s) => s.signer_role === r.value)
            );
            const isExpanded = expandedIndex === index;
            // Indica se há dados padrão preenchidos
            const hasDefaults = !!(signer.default_name || signer.default_email || signer.default_phone);

            return (
              <div
                key={index}
                className="rounded-lg border bg-card overflow-hidden"
              >
                {/* Linha principal: ordem, papel, método, controles */}
                <div className="flex items-center gap-2 p-3">
                  {/* Botão expandir/recolher */}
                  <button
                    type="button"
                    onClick={() => toggleExpand(index)}
                    className="h-5 w-5 flex items-center justify-center text-muted-foreground hover:text-foreground transition-transform"
                  >
                    <ChevronRight className={cn(
                      "h-3.5 w-3.5 transition-transform duration-200",
                      isExpanded && "rotate-90"
                    )} />
                  </button>

                  {/* Número da ordem */}
                  <Badge variant="outline" className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-xs">
                    {signer.sign_order}
                  </Badge>

                  {/* Papel */}
                  <Select
                    value={signer.signer_role}
                    onValueChange={(v) => handleRoleChange(index, v)}
                  >
                    <SelectTrigger className="h-8 flex-1 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {rolesForThis.map((r) => (
                        <SelectItem key={r.value} value={r.value}>
                          {r.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Indicador de dados padrão preenchidos */}
                  {hasDefaults && !isExpanded && (
                    <Badge variant="secondary" className="text-[10px] shrink-0">
                      Dados padrão
                    </Badge>
                  )}

                  {/* Método de entrega (só se provider selecionado tem métodos) */}
                  {visibleMethods.length > 0 && (
                    <Select
                      value={signer.delivery_method}
                      onValueChange={(v) => handleMethodChange(index, v)}
                    >
                      <SelectTrigger className="h-8 w-36 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {visibleMethods.map((m) => (
                          <SelectItem key={m.value} value={m.value}>
                            {m.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}

                  {/* Controles de ordem */}
                  <div className="flex flex-col gap-0.5">
                    <button
                      type="button"
                      onClick={() => handleMoveUp(index)}
                      disabled={index === 0}
                      className="h-4 w-4 flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-30"
                    >
                      <ChevronUp className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleMoveDown(index)}
                      disabled={index === signers.length - 1}
                      className="h-4 w-4 flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-30"
                    >
                      <ChevronDown className="h-3.5 w-3.5" />
                    </button>
                  </div>

                  {/* Remover */}
                  <button
                    type="button"
                    onClick={() => handleRemove(index)}
                    className="h-7 w-7 flex items-center justify-center rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>

                {/* Área expandida: campos de dados padrão */}
                {isExpanded && (
                  <div className="px-3 pb-3 pt-1 border-t bg-muted/30">
                    <p className="text-[11px] text-muted-foreground mb-2.5">
                      Preencha para carregar automaticamente no wizard de criação de contrato
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {/* Nome padrão */}
                      <div className="grid gap-1">
                        <Label className="text-xs text-muted-foreground">Nome</Label>
                        <Input
                          value={signer.default_name || ''}
                          onChange={(e) => handleDefaultFieldChange(index, 'default_name', e.target.value)}
                          placeholder="Nome completo"
                          className="h-8 text-sm"
                        />
                      </div>
                      {/* Email padrão */}
                      <div className="grid gap-1">
                        <Label className="text-xs text-muted-foreground">E-mail</Label>
                        <Input
                          value={signer.default_email || ''}
                          onChange={(e) => handleDefaultFieldChange(index, 'default_email', e.target.value)}
                          placeholder="email@exemplo.com"
                          type="email"
                          className="h-8 text-sm"
                        />
                      </div>
                      {/* Telefone padrão com seletor de país */}
                      <div className="grid gap-1">
                        <Label className="text-xs text-muted-foreground">Telefone</Label>
                        <PhoneInput
                          value={signer.default_phone || ''}
                          onChange={(value) => handleDefaultFieldChange(index, 'default_phone', value)}
                          placeholder="(11) 99999-9999"
                          className="[&_button]:h-8 [&_button]:w-[90px] [&_input]:h-8 [&_input]:text-sm"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
